/*
 * AI 填表员首页 · V3(双列工作台版)
 *
 * 严格按设计稿(AI填表员.pen 节点 BdhoJ)实现:
 *   顶栏 + 左「AI 引导对话面板」(460 宽) + 右(上传 dropzone + 我的模板 + 最近文档)。
 * 区别于 V2 单页流式 Hero:V3 把首页做成「进站即工作台」,降低上传/发起的心理门槛。
 *
 * 上传 / 积分拦截 / 生成 / 模板发起逻辑完全复用 V2(逐字搬运)。按红队结论 V3 自实现
 * 状态机、不抽 hook,避免改动 V1/V2 生产流量主体。视图层重写为双列。
 *
 * 入口三态由 aiClerk/index.tsx 路由决定:默认 V3,?v=1 → 旧 Home,?v=2 → HomeV2。
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { message, Button, Modal } from 'antd'
import {
  AlertOutlined,
  ArrowUpOutlined,
  AuditOutlined,
  CameraOutlined,
  CarOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  ContainerOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DiffOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  ExceptionOutlined,
  ExportOutlined,
  FileDoneOutlined,
  FileExcelFilled,
  FileFilled,
  FilePdfFilled,
  FilePptFilled,
  FileProtectOutlined,
  FileTextFilled,
  FileTextOutlined,
  FileWordFilled,
  FireOutlined,
  FlagOutlined,
  FormOutlined,
  FundOutlined,
  GiftFilled,
  HighlightOutlined,
  InboxOutlined,
  LoadingOutlined,
  MessageOutlined,
  NotificationOutlined,
  PaperClipOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  ProfileOutlined,
  ProjectOutlined,
  ReadOutlined,
  ReconciliationOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  ScanOutlined,
  ScheduleOutlined,
  SnippetsOutlined,
  SolutionOutlined,
  StarFilled,
  SwapOutlined,
  TableOutlined,
  TeamOutlined,
  ThunderboltFilled,
  ToolOutlined,
  UploadOutlined,
  UserOutlined,
  WarningOutlined
} from '@ant-design/icons'
import { xheyAi, SINGLE_QUERY_COST } from '../api'
import type { CreditsBalance } from '../api'
import { useCurrentFile } from '../ctx/FileCtx'
import {
  computeExcelDiff,
  diffToPromptText,
  readFileAsArrayBuffer,
  probeTemplateSampleMatch,
  MatchProbeResult
} from '../utils/excelDiff'
import { ensureSafeFile } from '../utils/sanitizeFileName'
import {
  OFFICE_ACCEPT_TYPES as ACCEPT_TYPES,
  validateOfficeFile
} from '../utils/officeFileTypes'
import {
  trackEnter,
  trackClick,
  trackResult,
  trackDialogShow,
  trackDialogClick
} from '../utils/track'
import { useCredits } from '../ctx/CreditsCtx'
import { usePayment, useVipKind, useAppSelector } from '../../../app/hooks'
import { useGroupPhotoNumFormated } from '../../../app/hooks/useGroupPhotoNumFormated'
import CreditsExhaustedModal from '../components/CreditsExhaustedModal'
import PointsPackModal from '../../components/PointsPackModal'
import PointsEntryChip from '../../components/PointsEntryChip'
import PointsStatusCard from '../../components/PointsStatusCard'
import { aiClerkPayContent } from '../utils/paymentContent'
import DemoReplayModal from '../demo/DemoReplayModal'
import { EditSkillModal } from '../components/EditSkillModal'
import CONFIG from '../../../constants/config'
import type { SkillItem, FileItem } from '../types'
// sessionStorage key 与 SamplePayload 复用旧 Home 的定义(同一份,避免 key 漂移导致
// work 页消费不到)
import { SAMPLE_DIFF_PROMPT_KEY, DESCRIBE_PROMPT_KEY } from './index'
import type { SamplePayload } from './index'
import './index.scss'
import './HomeV3.scss'
import HeroCarousel from './guide/HeroCarousel'
import dataSamplePhoto from './guide/data-sample.jpg'
import SupportedTypes from './guide/SupportedTypes'
import GiftModal from './guide/GiftModal'

function formatBytes(n?: number): string {
  if (!n || n < 0) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function formatSessionTime(input: string | number | undefined): string {
  if (!input) return ''
  const ts = typeof input === 'number' ? input : new Date(input).getTime()
  if (Number.isNaN(ts)) return ''
  const diff = Date.now() - ts
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return '刚刚'
  if (mins < 60) return `${mins} 分钟前`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} 小时前`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} 天前`
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// 文件类型 → antd6 实心图标查表（pickFileIconStyle 返回的 type 字符串）
const FILE_FILLED_ICONS: Record<string, React.ComponentType<any>> = {
  'file-excel': FileExcelFilled,
  'file-word': FileWordFilled,
  'file-ppt': FilePptFilled,
  'file-pdf': FilePdfFilled,
  'file-text': FileTextFilled,
  file: FileFilled
}
const FileTypeIcon = ({
  name,
  ...rest
}: {
  name: string
  [k: string]: any
}) => {
  const C = FILE_FILLED_ICONS[name]
  return C ? <C {...rest} /> : null
}

function pickFileIconStyle(filename: string): {
  type:
    | 'file-excel'
    | 'file-word'
    | 'file-pdf'
    | 'file-ppt'
    | 'file-text'
    | 'file'
  bg: string
  color: string
} {
  const ext = (filename.split('.').pop() || '').toLowerCase()
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv')
    return { type: 'file-excel', bg: '#ECFDF5', color: '#047857' }
  if (ext === 'docx' || ext === 'doc')
    return { type: 'file-word', bg: '#EFF6FF', color: '#1D4ED8' }
  if (ext === 'pdf')
    return { type: 'file-pdf', bg: '#FFEBEE', color: '#C62828' }
  if (ext === 'pptx' || ext === 'ppt')
    return { type: 'file-ppt', bg: '#FFF3E0', color: '#D84315' }
  if (ext === 'txt' || ext === 'md')
    return { type: 'file-text', bg: '#F5F5F5', color: '#616161' }
  return { type: 'file', bg: '#F5F5F5', color: '#757575' }
}

const XLSX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const XLS_MEDIA_TYPE = 'application/vnd.ms-excel'
const DOCX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const DOC_MEDIA_TYPE = 'application/msword'

function pickMediaType(filename: string): string {
  const ext = (filename.split('.').pop() || '').toLowerCase()
  if (ext === 'xlsx') return XLSX_MEDIA_TYPE
  if (ext === 'xls') return XLS_MEDIA_TYPE
  if (ext === 'docx') return DOCX_MEDIA_TYPE
  if (ext === 'doc') return DOC_MEDIA_TYPE
  return 'application/octet-stream'
}

const TRUST_NAMES = [
  '建工',
  '中铁',
  '华建',
  '天恒',
  '中联',
  '大成',
  '宏远',
  '鑫源',
  '恒通',
  '亿达'
]
const TRUST_COLORS = ['#6E3AF2', '#2E6BE6', '#16A06E', '#D9870B', '#E07B2A']

/**
 * chip 图标名(源老栈用 antd3 `<Icon type="xxx" />` 的字符串) → antd6 Outlined 组件映射表。
 * 重构版删了 LegacyIcon,动态图标改成按名字查表拿组件渲染。三套 chip 里出现过的图标名
 * 都要在此有对应项,查不到会渲染空白。
 */
const CHIP_ICON_MAP: Record<string, React.ComponentType<any>> = {
  profile: ProfileOutlined,
  schedule: ScheduleOutlined,
  team: TeamOutlined,
  warning: WarningOutlined,
  safety: SafetyOutlined,
  'file-protect': FileProtectOutlined,
  alert: AlertOutlined,
  exception: ExceptionOutlined,
  container: ContainerOutlined,
  'file-text': FileTextOutlined,
  project: ProjectOutlined,
  solution: SolutionOutlined,
  camera: CameraOutlined,
  snippets: SnippetsOutlined,
  inbox: InboxOutlined,
  tool: ToolOutlined,
  audit: AuditOutlined,
  read: ReadOutlined,
  fire: FireOutlined,
  'safety-certificate': SafetyCertificateOutlined,
  flag: FlagOutlined,
  fund: FundOutlined,
  diff: DiffOutlined,
  notification: NotificationOutlined,
  table: TableOutlined,
  'file-done': FileDoneOutlined,
  reconciliation: ReconciliationOutlined,
  car: CarOutlined,
  form: FormOutlined
}

/** 按 chip.icon 名查表渲染 Outlined 图标;查不到回落到 ProfileOutlined,避免空白 */
const ChipIcon = ({
  name,
  ...rest
}: {
  name: string
  [k: string]: any
}) => {
  const C = CHIP_ICON_MAP[name] || ProfileOutlined
  return <C {...rest} />
}

/**
 * 左栏「快速开始」chip 数据结构。
 * label:展示在 chip 上的短标题;message:点击后实际发送给 AI 的完整描述。
 * 拆成两个字段的原因:安全 / 拼图场景的描述较长,直接铺在 chip 上会撑爆按钮。
 */
interface QuickChip {
  icon: string
  label: string
  message: string
}

/** 默认 chips:通用场景(无来源或来源未知时展示) */
const QUICK_CHIPS_DEFAULT: QuickChip[] = [
  {
    icon: 'profile',
    label: '施工验收台账',
    message:
      '做一份本月施工验收台账，每条记录配现场照片，时间和地点从照片水印数据里取'
  },
  {
    icon: 'schedule',
    label: '安全巡检记录表',
    message:
      '做一张安全巡检记录表，每条巡检点配一张现场照片，自动填拍摄时间和地点'
  },
  {
    icon: 'team',
    label: '每日考勤表',
    message: '做一张每日考勤表，按人员自动配每天的打卡照片，包含签到时间和地点'
  },
  {
    icon: 'inbox',
    label: '材料进场验收单',
    message:
      '做一份材料进场验收单，每批材料配进场照片，自动填进场时间、规格数量和验收结论'
  },
  {
    icon: 'tool',
    label: '设备巡检记录表',
    message:
      '做一张设备巡检记录表，每台设备配巡检照片，自动填巡检时间、地点和运行状态'
  },
  {
    icon: 'audit',
    label: '质量检查记录表',
    message:
      '做一张质量检查记录表，每个检查项配现场照片，自动填检查时间、地点和检查结论'
  },
  {
    icon: 'file-done',
    label: '隐蔽工程验收记录',
    message:
      '做一份隐蔽工程验收记录，主要有验收部位、隐蔽内容、验收结论、验收人等栏，每条配隐蔽前的现场照片，时间地点从照片水印取'
  },
  {
    icon: 'reconciliation',
    label: '安全技术交底记录',
    message:
      '做一份安全技术交底记录，包含交底项目、交底内容、交底人、接受人和日期等栏，配交底现场照片'
  },
  {
    icon: 'car',
    label: '车辆进出场登记表',
    message:
      '做一份车辆进出场登记表，包含车牌号、进出时间、装载物、司机等栏，每条配车辆现场照片，时间从照片水印取'
  },
  {
    icon: 'form',
    label: '材料领用登记表',
    message:
      '做一份材料领用登记表，包含材料名称、规格、领用数量、领用人、日期等栏，每条配领用现场照片'
  }
]

/** 来自「AI 安全隐患台账」入口(?from=safety):聚焦安全文书 */
const QUICK_CHIPS_SAFETY: QuickChip[] = [
  {
    icon: 'warning',
    label: '整改通知单',
    message:
      '帮我做一份整改通知单，把台账截图里的隐患描述、整改要求、责任人和整改期限自动填进去，每条隐患附上现场照片'
  },
  {
    icon: 'safety',
    label: '安全巡查记录表',
    message:
      '帮我做一份安全巡查记录表，每个巡查点配一张现场照片，时间和地点从照片水印里取，隐患描述和处理状态从台账数据里填'
  },
  {
    icon: 'file-protect',
    label: '安全生产月报',
    message:
      '帮我做一份本月安全生产月报，把隐患台账数据整理成汇总表格，统计隐患数量、整改率，配典型现场照片'
  },
  {
    icon: 'alert',
    label: '隐患整改回复单',
    message:
      '帮我做一份隐患整改回复单，按整改通知单的内容逐条回复整改结果，每条附上整改后的现场照片作为佐证'
  },
  {
    icon: 'exception',
    label: '安全事故报告',
    message:
      '帮我做一份安全事故报告，把事故经过、原因分析和处理措施填好，附现场照片、发生时间和地点'
  },
  {
    icon: 'container',
    label: '隐患台账汇总表',
    message:
      '帮我做一份隐患台账汇总表，把各条隐患的描述、等级、责任人和整改状态整理成汇总表格，配典型现场照片'
  },
  {
    icon: 'read',
    label: '安全教育培训记录',
    message:
      '帮我做一份安全教育培训记录，包含培训主题、培训内容、参训人员、培训时间地点等栏，配培训现场照片'
  },
  {
    icon: 'fire',
    label: '消防检查记录表',
    message:
      '帮我做一份消防检查记录表，包含检查部位、检查项目、检查结果、整改要求等栏，每条配现场照片，时间地点从水印取'
  },
  {
    icon: 'safety-certificate',
    label: '危险作业审批表',
    message:
      '帮我做一份危险作业审批表，包含作业类型、作业内容、风险等级、安全措施、审批人等栏，配作业现场照片'
  },
  {
    icon: 'flag',
    label: '应急演练记录表',
    message:
      '帮我做一份应急演练记录表，包含演练科目、演练时间地点、参演人员、演练过程和评估等栏，每步配现场照片'
  }
]

/** 来自「照片拼图汇报」入口(?from=jigsaw):聚焦照片汇报文档 */
const QUICK_CHIPS_JIGSAW: QuickChip[] = [
  {
    icon: 'file-text',
    label: '施工日报',
    message:
      '帮我做一份今日施工日报，把拼图里的现场照片和作业信息——施工部位、作业内容、完成情况——自动整理填入，时间和地点从照片水印数据里取'
  },
  {
    icon: 'project',
    label: '项目周报',
    message:
      '帮我做一份本周项目周报，按施工进度分段整理现场照片，配上各阶段作业时间、地点和完成情况，生成可交付的汇报文档'
  },
  {
    icon: 'solution',
    label: '监理日志',
    message:
      '帮我做一份今日监理日志，按时间顺序记录现场巡查情况，每条记录配一张现场照片，地点和时间从水印数据里取'
  },
  {
    icon: 'camera',
    label: '现场签到表',
    message:
      '帮我做一份现场签到表，每位作业人员对应一张签到照片，自动从团队照片里按人名匹配，填上签到时间和地点'
  },
  {
    icon: 'snippets',
    label: '每日工作汇报',
    message:
      '帮我做一份每日工作汇报，把今天的现场照片按作业内容归类，自动填作业时间、地点和完成情况'
  },
  {
    icon: 'schedule',
    label: '巡检记录表',
    message:
      '帮我做一张巡检记录表，每个巡检点配一张现场照片，自动填巡检时间、地点和检查结果'
  },
  {
    icon: 'fund',
    label: '工程形象进度',
    message:
      '帮我做一份工程形象进度记录，按施工部位/分项整理现场照片，配上形象进度描述、完成比例和拍摄时间地点'
  },
  {
    icon: 'diff',
    label: '整改前后对比',
    message:
      '帮我做一份隐患整改前后对比表，每条隐患左右并列整改前、整改后现场照片，配隐患描述、整改说明和时间'
  },
  {
    icon: 'notification',
    label: '班前安全会记录',
    message:
      '帮我做一份班前安全会记录，包含会议时间地点、参会人员、交底内容、注意事项等栏，配班前会现场照片'
  },
  {
    icon: 'table',
    label: '材料验收台账',
    message:
      '帮我做一份材料验收台账，包含材料名称、规格型号、进场数量、验收结论、验收日期等栏，每条配材料现场照片'
  }
]

// ════════ 精修版(右栏介绍区)数据,严格按设计稿 r9Wq8c 文案 ════════

/** 照片自带的字段(数据映射:照片 → 表格的左侧来源) */
const PF_PHOTO_FIELDS = [
  '拍摄时间',
  '经纬度',
  '真实海拔',
  '天气',
  '作业人员',
  '水印名称'
]

/** 台账表头(数据映射:照片 → 表格的右侧目标) */
const PF_LEDGER_HEADERS = ['作业人员', '拍摄时间', '真实海拔', '天气', '照片']
/** 台账表 mock 数据(前 4 列文字,第 5 列「照片」渲染为缩略图占位) */
const PF_LEDGER_ROWS = [
  ['张伟', '2026-04-17 09:12', '576.4 m', '晴'],
  ['李娜', '2026-04-17 10:05', '581.2 m', '晴'],
  ['王芳', '2026-04-17 11:20', '588.7 m', '多云']
]

/** 2 个核心特性(协作共创 / 对话式优化),每个带 eyebrow + 3 条勾选 */
const PF_FEATURES: Array<{
  eyebrow: string
  accent: string
  accentBg: string
  title: string
  desc: string
  checks: string[]
}> = [
  {
    eyebrow: '不只是生成，是真填对',
    accent: '#6E3AF2',
    accentBg: '#F1ECFE',
    title: '从团队照片库，挑对的照片填进去',
    desc: 'AI 读懂每一列要什么，再按拍摄时间、地点、人物从照片库取对的那一张，每格都对位到正确的照片，填得准、对得上。',
    checks: [
      '按时间、地点、人物自动匹配',
      '整段 Word 汇报也能自动写好',
      'Excel 表格、Word 汇报、照片拼图都支持'
    ]
  },
  {
    eyebrow: '边核对边改，越改越准',
    accent: '#2E6BE6',
    accentBg: '#EAF1FE',
    title: '填得不对？在预览区说一句就改',
    desc: 'AI 不确定时会先弹窗问你确认；生成后想调整，在预览区选中单元格说一句，它只改该改的格，不打乱其它已填好的内容。',
    checks: [
      'AI 不确定时先问你确认',
      '预览区选中，说一句就重填',
      '满意了存成模板，下次一键复用'
    ]
  }
]

/** 四步流程:从上传到交付 */
const PF_STEPS: Array<{
  icon: React.ComponentType<any>
  title: string
  desc: string
}> = [
  {
    icon: UploadOutlined,
    title: '上传文档',
    desc: '拖入 Word / Excel 模板，或直接描述需求'
  },
  {
    icon: ScanOutlined,
    title: 'AI 识别表头',
    desc: '自动读懂每一列、每一段要填什么'
  },
  {
    icon: UserOutlined,
    title: '跟你确认照片/人名',
    desc: 'AI 先列好每行配哪张照片，你扫一眼确认'
  },
  {
    icon: CheckCircleOutlined,
    title: '一键生成成品',
    desc: '确认后 AI 帮你填好，导出 Word / Excel'
  }
]

/** 预览 / 编辑页支持的能力(对应 work 页工具栏 + 对话编辑),右栏功能介绍段 */
const PF_CAPS: Array<{
  icon: React.ComponentType<any>
  title: string
  desc: string
  color: string
  bg: string
}> = [
  {
    icon: EditOutlined,
    title: 'AI 帮你填整列 / 整行',
    desc: '选中要填的列或行，AI 一次性填好，不用一格格点',
    color: '#6E3AF2',
    bg: '#F1ECFE'
  },
  {
    icon: TableOutlined,
    title: '增删行列、改单元格',
    desc: '表格结构随手调整，不用切回 Excel / Word',
    color: '#2E6BE6',
    bg: '#EAF1FE'
  },
  {
    icon: PictureOutlined,
    title: '一张图不对，一键换',
    desc: '选中单元格，重新从团队照片库挑对的那张',
    color: '#16A06E',
    bg: '#E6F6EF'
  },
  {
    icon: DatabaseOutlined,
    title: '批量填充',
    desc: '从一批数据，一次性填好几十上百行',
    color: '#D9870B',
    bg: '#FBEFD6'
  },
  {
    icon: HighlightOutlined,
    title: '对话调美观',
    desc: '说一句，对齐、底色、字号跟着调，表格更整齐好看',
    color: '#6E3AF2',
    bg: '#F1ECFE'
  },
  {
    icon: ExportOutlined,
    title: '一键导出 / 存模板',
    desc: '成稿直接下载 Word / Excel，或存成模板下次复用',
    color: '#2E6BE6',
    bg: '#EAF1FE'
  }
]

const HomeV3: React.FC = () => {
  const navigate = useNavigate()
  // 入口来源:从 URL 的 ?from= 读取(safety=「AI 安全隐患台账」入口 / jigsaw=「照片拼图汇报」入口),
  // 据此切换左栏快捷入口展示哪一组文书。运行时校验取值,未知 / 缺失来源回落到通用组。
  const { search } = useLocation()
  const rawFrom = new URLSearchParams(search).get('from')
  const entrySource: 'safety' | 'jigsaw' | null =
    rawFrom === 'safety' || rawFrom === 'jigsaw' ? rawFrom : null
  const { setFile } = useCurrentFile()
  const { credits, refresh: refreshCredits } = useCredits()
  const payment = usePayment()
  const vipKind = useVipKind()
  // 当前团队 id:作为下方加载 effect 的依赖,团队切换时触发技能 / 最近文档重新拉取。
  // 重构版 group slice 是 RTK 普通对象,直接读 state.group.groupId(非 Immutable .get)。
  const groupId = useAppSelector((state) => state.group.groupId)
  const canExportShare =
    vipKind === CONFIG.VIP_KIND.VVIP ||
    vipKind === CONFIG.VIP_KIND.SVIP ||
    vipKind === CONFIG.VIP_KIND.EVIP
  const [creditsModalVisible, setCreditsModalVisible] = useState(false)
  const [pointsPackVisible, setPointsPackVisible] = useState(false)
  const [pointsCardVisible, setPointsCardVisible] = useState(false)
  const [demoVisible, setDemoVisible] = useState(false)
  const [avatarOffset, setAvatarOffset] = useState(0)
  const [genText, setGenText] = useState('')
  const [skills, setSkills] = useState<SkillItem[]>([])
  const [skillsLoading, setSkillsLoading] = useState(true)
  const [editingSkill, setEditingSkill] = useState<SkillItem | null>(null)
  const [recentFiles, setRecentFiles] = useState<FileItem[]>([])
  const [recentFilesLoading, setRecentFilesLoading] = useState(true)
  // 被余额拦截的发起动作;续费成功后自动重试
  const pendingStartRef = useRef<null | 'upload' | SkillItem>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const sampleInputRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOverTemplate, setDragOverTemplate] = useState(false)
  const [dragOverSample, setDragOverSample] = useState(false)
  /** 用户选好的模板——延迟到点"开始识别"再上传,让用户有机会先添加样例 */
  const [templateFile, setTemplateFile] = useState<File | null>(null)
  /** 用户预先选好的"参考样例"——只存 File,不上传,diff 算完即抛 */
  const [sampleFile, setSampleFile] = useState<File | null>(null)
  const [probeResult, setProbeResult] = useState<MatchProbeResult | null>(null)
  const [probing, setProbing] = useState(false)
  const [startModalOpen, setStartModalOpen] = useState(false)
  const [giftOpen, setGiftOpen] = useState(false)
  // 团队照片总数(右栏 dropzone 副标数字),与顶栏 Header 统计同源同算法;未加载时为 null
  const photoNum = useGroupPhotoNumFormated()

  const loadSkills = useCallback(async () => {
    setSkillsLoading(true)
    try {
      const list = await xheyAi.listUserSkills()
      setSkills(
        [...list].sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0))
      )
    } catch {
      setSkills([])
    } finally {
      setSkillsLoading(false)
    }
  }, [])

  const loadRecentFiles = useCallback(async () => {
    setRecentFilesLoading(true)
    try {
      const list = await xheyAi.listRecentFiles({ limit: 10 })
      setRecentFiles(
        [...list].sort((a, b) => (b.mtime_ms || 0) - (a.mtime_ms || 0))
      )
    } catch {
      setRecentFiles([])
    } finally {
      setRecentFilesLoading(false)
    }
  }, [])

  // groupId 加入依赖:切换团队后重新加载「我的模板」与「最近填好的文档」,
  // 否则会残留上一个团队的数据(两个列表都按团队隔离,后端按 group_id 返回)。
  useEffect(() => {
    loadSkills()
    loadRecentFiles()
  }, [groupId, loadSkills, loadRecentFiles])

  // 信任栏头像轮播
  useEffect(() => {
    const t = setInterval(
      () => setAvatarOffset((o) => (o + 1) % TRUST_NAMES.length),
      2200
    )
    return () => clearInterval(t)
  }, [])

  // 页面进入埋点(只 mount 时上报一次)
  useEffect(() => {
    trackEnter('formFiller', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.pageview.ai_enter_web_ai_form_filler'
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 上传文档半屏进入埋点(Modal false → true 时上报一次)
  const startModalOpenRef = useRef(false)
  useEffect(() => {
    if (startModalOpen && !startModalOpenRef.current) {
      trackEnter('uploadDoc', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.pageview.ai_enter_web_ai_upload_doc',
        enterFrom: 'web_ai_form_filler'
      })
    }
    startModalOpenRef.current = startModalOpen
  }, [startModalOpen])

  // 新用户福利弹窗展示埋点
  useEffect(() => {
    if (giftOpen) {
      trackDialogShow('formFiller', 'gift_modal', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.pageview.ai_dialog_show_web_ai_form_filler'
      })
    }
  }, [giftOpen])

  // 预匹配探测:模板 + 样例都是 xlsx 时跑一次轻量字段对齐检测。debounce 300ms。
  useEffect(() => {
    if (!templateFile || !sampleFile) {
      setProbeResult(null)
      setProbing(false)
      return
    }
    const tExt = (templateFile.name.split('.').pop() || '').toLowerCase()
    const sExt = (sampleFile.name.split('.').pop() || '').toLowerCase()
    const isExcelPair =
      ['xlsx', 'xls'].includes(tExt) && ['xlsx', 'xls'].includes(sExt)
    if (!isExcelPair) {
      setProbeResult(null)
      setProbing(false)
      return
    }
    let cancelled = false
    const timer = setTimeout(async () => {
      if (cancelled) return
      setProbing(true)
      try {
        const [tBuf, sBuf] = await Promise.all([
          readFileAsArrayBuffer(templateFile),
          readFileAsArrayBuffer(sampleFile)
        ])
        if (cancelled) return
        const result = await probeTemplateSampleMatch(tBuf, sBuf)
        if (cancelled) return
        setProbeResult(result)
      } catch (e) {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.warn('[aiClerk] 预匹配探测失败:', e)
        setProbeResult({
          matchPercent: 0,
          matchedHeaders: 0,
          totalHeaders: 0,
          reversed: false,
          templateFilledCount: 0,
          sampleFilledCount: 0,
          parseError: e instanceof Error ? e.message : String(e)
        })
      } finally {
        if (!cancelled) setProbing(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [templateFile, sampleFile])

  const handleSwapTemplateAndSample = useCallback(() => {
    setTemplateFile((prev) => {
      const wasSample = sampleFile
      setSampleFile(prev)
      return wasSample
    })
  }, [sampleFile])

  const handlePickTemplate = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.click()
  }, [])

  const handleClearTemplateInModal = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (uploading) return
      trackClick('uploadDoc', 'remove_file', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
      })
      setTemplateFile(null)
    },
    [uploading]
  )

  const handleTemplateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      const err = validateOfficeFile(file)
      if (err) {
        message.error(err)
        return
      }
      if (startModalOpen) {
        trackClick('uploadDoc', 'upload_area', {
          requirement_id: 1103,
          track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
        })
      } else {
        trackClick('formFiller', 'upload_area', {
          requirement_id: 1103,
          track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
        })
      }
      setTemplateFile(file)
      setStartModalOpen(true)
    },
    [startModalOpen]
  )

  const handleStartModalClose = useCallback(() => {
    if (uploading) return
    trackClick('uploadDoc', 'close', {
      requirement_id: 1103,
      track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
    })
    setStartModalOpen(false)
    setTemplateFile(null)
    setSampleFile(null)
  }, [uploading])

  const handlePickSample = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    trackClick('uploadDoc', 'upload_sample', {
      requirement_id: 1103,
      track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
    })
    if (sampleInputRef.current) sampleInputRef.current.click()
  }, [])

  const handleSampleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      const err = validateOfficeFile(file)
      if (err) {
        message.error(`参考样例：${err}`)
        return
      }
      setSampleFile(file)
    },
    []
  )

  const handleSampleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOverSample(true)
  }, [])
  const handleSampleDragLeave = useCallback(() => {
    setDragOverSample(false)
  }, [])
  const handleSampleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverSample(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    const err = validateOfficeFile(file)
    if (err) {
      message.error(`参考样例：${err}`)
      return
    }
    setSampleFile(file)
  }, [])

  const handleTemplateDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOverTemplate(true)
  }, [])
  const handleTemplateDragLeave = useCallback(() => {
    setDragOverTemplate(false)
  }, [])
  const handleTemplateDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverTemplate(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    const err = validateOfficeFile(file)
    if (err) {
      message.error(err)
      return
    }
    trackClick('formFiller', 'upload_area', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
      string1: 'drag_drop'
    })
    setTemplateFile(file)
    setStartModalOpen(true)
  }, [])

  /**
   * 用户点击发起会话前:确保 credits 已就绪(瞬态故障时主动补 refresh 一次)。
   *   - { ok:true, credits }:有效余额
   *   - { ok:false }:已 message.error「网络错误，请刷新重试」,调用方 return
   */
  const ensureCreditsReady = useCallback(async (): Promise<{
    ok: boolean
    credits: CreditsBalance | null
  }> => {
    let c: CreditsBalance | null = credits
    if (!c || c.available == null) {
      c = await refreshCredits()
    }
    if (!c || c.available == null) {
      message.error('网络错误，请刷新重试')
      return { ok: false, credits: null }
    }
    return { ok: true, credits: c }
  }, [credits, refreshCredits])

  // 真正的上传 + diff + 跳转——点"开始识别"才触发
  const handleStart = useCallback(
    async (skipGuard = false) => {
      if (uploading) return
      if (!skipGuard) {
        const ready = await ensureCreditsReady()
        if (!ready.ok) return
        if ((ready.credits?.available ?? 0) <= 0) {
          pendingStartRef.current = 'upload'
          setCreditsModalVisible(true)
          return
        }
      }
      if (!templateFile) {
        message.error('请先选择要填写的模板')
        return
      }
      trackClick('uploadDoc', 'start_recognition', {
        requirement_id: 1103,
        track_sign: 'aai.b8135.click.ai_click_web_ai_upload_doc'
      })
      const tExt = (templateFile.name.split('.').pop() || '').toLowerCase()
      const sExt = sampleFile
        ? (sampleFile.name.split('.').pop() || '').toLowerCase()
        : ''
      const isExcelPair =
        ['xlsx', 'xls'].includes(tExt) && ['xlsx', 'xls'].includes(sExt)
      const isWordPair =
        ['docx', 'doc'].includes(tExt) && ['docx', 'doc'].includes(sExt)
      const sampleUsable = !!sampleFile && (isExcelPair || isWordPair)
      if (sampleFile && !sampleUsable) {
        message.warning(
          '参考样例必须与模板同类型（Excel↔Excel / Word↔Word），本次忽略样例继续上传'
        )
      }
      setUploading(true)
      try {
        const safeTemplate = ensureSafeFile(templateFile)
        const safeSample =
          sampleUsable && sampleFile ? ensureSafeFile(sampleFile) : null
        const t0 = Date.now()
        let templateCostTime = 0
        let sampleCostTime = 0
        const [uploadResult, sampleUploadResult, templateBuf, sampleBuf] =
          await Promise.all([
            xheyAi.upload(safeTemplate).then((r) => {
              templateCostTime = Date.now() - t0
              return r
            }),
            safeSample
              ? xheyAi.uploadAttachment(safeSample).then((r) => {
                  sampleCostTime = Date.now() - t0
                  return r
                })
              : Promise.resolve(null),
            isExcelPair
              ? readFileAsArrayBuffer(templateFile)
              : Promise.resolve(null),
            isExcelPair && sampleFile
              ? readFileAsArrayBuffer(sampleFile)
              : Promise.resolve(null)
          ])
        const tFmt = tExt === 'docx' || tExt === 'xlsx' ? tExt : 'other'
        trackResult('uploadDoc', 'upload_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
          actionResult: 'success',
          string1: tFmt,
          number1: Math.round(safeTemplate.size / 1024),
          bool1: !!sampleUploadResult,
          costTime: templateCostTime
        })
        if (safeSample && sampleUploadResult) {
          const sFmt = sExt === 'docx' || sExt === 'xlsx' ? sExt : 'other'
          trackResult('uploadDoc', 'upload_sample', {
            requirement_id: 1103,
            track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
            actionResult: 'success',
            string1: sFmt,
            number1: Math.round(safeSample.size / 1024),
            costTime: sampleCostTime
          })
        }
        const { fileUrl, fileName, filePath, refreshUrl } = uploadResult
        if (sampleUsable && safeSample && sampleUploadResult) {
          let diffText = ''
          if (isExcelPair && templateBuf && sampleBuf) {
            try {
              const diff = await computeExcelDiff(
                templateBuf,
                sampleBuf,
                safeTemplate.name,
                safeSample.name
              )
              diffText = diffToPromptText(diff)
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('[aiClerk] 样例 diff 计算失败:', e)
              const detail = e instanceof Error ? e.message : String(e)
              message.warning({
                content: `参考样例解析失败：${detail}（继续按附件方式发给 agent）`,
                duration: 6
              })
            }
          }
          const payload: SamplePayload = {
            diffText,
            attachment: {
              url: sampleUploadResult.fileUrl,
              name: safeSample.name,
              mediaType: pickMediaType(safeSample.name)
            }
          }
          window.sessionStorage.setItem(
            SAMPLE_DIFF_PROMPT_KEY,
            JSON.stringify(payload)
          )
        } else {
          window.sessionStorage.removeItem(SAMPLE_DIFF_PROMPT_KEY)
        }
        setFile({ fileUrl, fileName, filePath, refreshUrl })
        try {
          window.localStorage.removeItem('aiClerk:conversationId')
        } catch {
          /* 忽略 */
        }
        navigate('/ai/clerk/work')
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e)
        message.error(`上传失败：${errMsg}`)
        const tFmtRaw = (templateFile.name.split('.').pop() || '').toLowerCase()
        const tFmt =
          tFmtRaw === 'docx' || tFmtRaw === 'xlsx' ? tFmtRaw : 'other'
        trackResult('uploadDoc', 'upload_doc', {
          requirement_id: 1103,
          track_sign: 'aai.b8135.click.ai_result_web_ai_upload_doc',
          actionResult: 'fail',
          string1: tFmt,
          number1: Math.round(templateFile.size / 1024),
          bool1: !!sampleFile,
          errorMsg: errMsg
        })
        setUploading(false)
      }
    },
    [navigate, uploading, setFile, templateFile, sampleFile, ensureCreditsReady]
  )

  const startTemplate = useCallback(
    async (sk: SkillItem) => {
      trackClick('formFiller', 'continue_use_template', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
        string1: sk.name
      })
      try {
        const result = await xheyAi.useSkill(sk.name)
        try {
          window.localStorage.removeItem('aiClerk:conversationId')
        } catch {
          /* 忽略 */
        }
        if (result.hasTemplate) {
          setFile({
            fileUrl: result.fileUrl,
            fileName: result.fileName,
            filePath: result.filePath,
            refreshUrl: result.refreshUrl
          })
        }
        const qs =
          `?skill=${encodeURIComponent(sk.name)}` +
          `&skillTitle=${encodeURIComponent(sk.display_name || sk.name)}`
        navigate(`/ai/clerk/work${qs}`)
      } catch (e) {
        message.error(
          `继续使用失败: ${e instanceof Error ? e.message : String(e)}`
        )
      }
    },
    [navigate, setFile]
  )

  // 续费成功后放行容错:续上被拦截的上传或模板发起
  const runPendingStart = useCallback(() => {
    const pending = pendingStartRef.current
    if (!pending) return
    pendingStartRef.current = null
    if (pending === 'upload') {
      handleStart(true)
    } else {
      startTemplate(pending)
    }
  }, [handleStart, startTemplate])

  // 会员升级成功(vipKind 变化)+ 有待发起动作 → 刷新余额 + 放行容错续会话
  const prevVipKindRef = useRef(vipKind)
  useEffect(() => {
    if (prevVipKindRef.current !== vipKind) {
      refreshCredits()
      if (pendingStartRef.current) {
        runPendingStart()
      }
    }
    prevVipKindRef.current = vipKind
  }, [vipKind, refreshCredits, runPendingStart])

  const handleGenerate = useCallback(
    async (overrideText?: string) => {
      if (uploading) return
      const text = (
        typeof overrideText === 'string' ? overrideText : genText
      ).trim()
      if (!text) return
      // 「用一句话生成空白表」入口:点击 → 余额校验 → upload(空文档,只带描述) → 跳 work
      trackClick('formFiller', 'generate_form', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
        number1: text.length
      })
      const ready = await ensureCreditsReady()
      if (!ready.ok) return
      if ((ready.credits?.available ?? 0) <= 0) {
        pendingStartRef.current = 'upload'
        setCreditsModalVisible(true)
        return
      }
      setUploading(true)
      const t0 = Date.now()
      try {
        const { fileUrl, fileName, filePath, refreshUrl } =
          await xheyAi.upload()
        setFile({ fileUrl, fileName, filePath, refreshUrl })
        try {
          window.sessionStorage.setItem(DESCRIBE_PROMPT_KEY, text)
        } catch {
          /* 忽略 */
        }
        try {
          window.localStorage.removeItem('aiClerk:conversationId')
        } catch {
          /* 忽略 */
        }
        trackResult('formFiller', 'generate_form', {
          requirement_id: 1103,
          track_sign: 'aai.b8134.click.ai_result_web_ai_form_filler',
          actionResult: 'success',
          number1: text.length,
          costTime: Date.now() - t0
        })
        navigate('/ai/clerk/work')
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e)
        message.error(`生成失败：${errMsg}`)
        trackResult('formFiller', 'generate_form', {
          requirement_id: 1103,
          track_sign: 'aai.b8134.click.ai_result_web_ai_form_filler',
          actionResult: 'fail',
          number1: text.length,
          costTime: Date.now() - t0,
          errorMsg: errMsg
        })
        setUploading(false)
      }
    },
    [uploading, genText, ensureCreditsReady, setFile, navigate]
  )

  const handleWatchDemo = useCallback(() => {
    trackClick('formFiller', 'view_demo', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
    })
    setDemoVisible(true)
  }, [])

  const handleDescribeCta = useCallback(() => {
    trackClick('formFiller', 'describe_cta', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
    })
    const el = inputRef.current
    if (!el) return
    el.focus()
    el.classList.add('is-highlight')
    const onEnd = () => {
      el.classList.remove('is-highlight')
      el.removeEventListener('animationend', onEnd)
    }
    el.addEventListener('animationend', onEnd)
  }, [])

  // 积分入口 CTA:增购积分包(超级会员)/ 升级会员(其它),参照 work handlePointsCta
  const handlePointsCta = useCallback(
    (ctx: { action: 'upgrade' | 'addon' }) => {
      setPointsCardVisible(false)
      if (ctx.action === 'addon') {
        setPointsPackVisible(true)
      } else {
        payment.openVipByKind(
          vipKind || CONFIG.VIP_KIND.VIP,
          'aiFormFiller',
          false,
          aiClerkPayContent({
            isVip: vipKind > 0,
            vipKind,
            usedPoints: credits?.consumed ?? 0
          })
        )
      }
    },
    [payment, vipKind, credits]
  )

  const handleChipClick = useCallback(
    (chip: QuickChip) => {
      trackClick('formFiller', 'quick_chip', {
        requirement_id: 1103,
        track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
        string1: chip.label,
        string2: entrySource || 'default'
      })
      // 输入框回显短标题,真正发送给 AI 的是完整描述(message)
      setGenText(chip.label)
      handleGenerate(chip.message)
    },
    [handleGenerate, entrySource]
  )

  const handleQuickFill = useCallback((name: string) => {
    trackClick('formFiller', 'supported_type_chip', {
      requirement_id: 1103,
      track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler',
      string1: name
    })
    setGenText(`做一份本月${name}，使用我的团队已有照片`)
    const el = inputRef.current
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    el.focus()
    el.classList.remove('is-highlight')
    void el.offsetWidth
    el.classList.add('is-highlight')
    const onEnd = () => {
      el.classList.remove('is-highlight')
      el.removeEventListener('animationend', onEnd)
    }
    el.addEventListener('animationend', onEnd)
  }, [])

  // 两个接口都完成后才判断「我的模板 / 最近文档」要不要展示,加载期间不渲染避免闪烁
  const isLoading = skillsLoading || recentFilesLoading
  const hasHistory = !isLoading && (skills.length > 0 || recentFiles.length > 0)

  const photoNumText = photoNum
    ? photoNum.unit === '万'
      ? `${photoNum.value}万`
      : `${photoNum.value}`
    : '28 万'

  return (
    <div className="aiClerk-homeV3">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_TYPES}
        hidden
        onChange={handleTemplateChange}
      />

      {/* ── 主体:左 AI 对话面板(含上传入口) + 右 引导/模板/最近 ──
          顶栏删除:全站已有顶栏(团队/会员/头像),V3 不再重复一层 */}
      <div className="aiClerk-homeV3__body">
        {/* 左:上传入口(缩小,置顶) + AI 引导对话 */}
        <aside className="aiClerk-homeV3__chat">
          {/* 标题对齐 work 页 ChatPanel header:✦ 紫渐变圆 + 名称 */}
          <div className="aiClerk-homeV3__chat-head">
            <span className="aiClerk-homeV3__chat-avatar">✦</span>
            <div className="aiClerk-homeV3__chat-headtext">
              <div className="aiClerk-homeV3__chat-name">AI 填表员</div>
              <div className="aiClerk-homeV3__chat-status">
                在线 · 帮你把照片填进表格
              </div>
            </div>
          </div>

          <div className="aiClerk-homeV3__chat-body">
            {/* 上传区:头部说明(价值主张) + 上传卡 */}
            <div className="aiClerk-homeV3__sec-hint">
              上传模板，我从团队照片库按水印内容、时间挑好对应照片，整份自动填好——省下大半天手工。
            </div>
            <div
              className={`aiClerk-homeV3__upload-card${
                dragOverTemplate ? ' is-drag-over' : ''
              }`}
              onClick={handlePickTemplate}
              onDragOver={handleTemplateDragOver}
              onDragLeave={handleTemplateDragLeave}
              onDrop={handleTemplateDrop}
            >
              <span className="aiClerk-homeV3__upload-card-icon">
                <CloudUploadOutlined />
              </span>
              <div className="aiClerk-homeV3__upload-card-text">
                <div className="aiClerk-homeV3__upload-card-title">
                  上传要填的 Word / Excel
                </div>
                <div className="aiClerk-homeV3__upload-card-sub">
                  拖进来或点击 · 照片 AI 来配
                </div>
              </div>
              <button
                type="button"
                className="aiClerk-homeV3__upload-card-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  handlePickTemplate()
                }}
                disabled={uploading}
              >
                上传
              </button>
            </div>

            {/* 快捷生成区:头部说明 + 快捷 chip */}
            <div className="aiClerk-homeV3__sec-hint">
              {entrySource === 'safety'
                ? '针对安全隐患台账，可以直接生成这些文书：'
                : entrySource === 'jigsaw'
                  ? '我还可以生成拼图汇报文档：'
                  : '没有文档模板也能填写：在下面说一句要什么表，我先做一份，再帮你填好。'}
            </div>

            <div className="aiClerk-homeV3__chat-chips">
              {(entrySource === 'safety'
                ? QUICK_CHIPS_SAFETY
                : entrySource === 'jigsaw'
                  ? QUICK_CHIPS_JIGSAW
                  : QUICK_CHIPS_DEFAULT
              ).map((c) => (
                <button
                  key={c.label}
                  type="button"
                  className="aiClerk-homeV3__chip"
                  onClick={() => handleChipClick(c)}
                  disabled={uploading}
                >
                  <ChipIcon
                    name={c.icon}
                    className="aiClerk-homeV3__chip-icon"
                  />
                  <span className="aiClerk-homeV3__chip-text">{c.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 输入框样式对齐 work 页 ChatPanel:灰底圆角 textarea + 左下纸夹 + 右下发送 */}
          <div className="aiClerk-homeV3__chat-input">
            <div className="aiClerk-homeV3__input-wrap">
              <textarea
                ref={inputRef}
                className="aiClerk-homeV3__input"
                placeholder={`例如：把 ${
                  new Date().getMonth() + 1
                } 月的现场照片整理成验收 / 巡检记录表，要照片、时间、地点……`}
                value={genText}
                onChange={(e) => setGenText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleGenerate()
                  }
                }}
                disabled={uploading}
              />
              <button
                type="button"
                className="aiClerk-homeV3__attach-btn"
                onClick={handlePickTemplate}
                disabled={uploading}
                aria-label="上传文档"
              >
                <PaperClipOutlined />
              </button>
              <button
                type="button"
                className="aiClerk-homeV3__send-btn"
                onClick={() => handleGenerate()}
                disabled={uploading || !genText.trim()}
                aria-label="生成"
              >
                <ArrowUpOutlined />
              </button>
            </div>
            {/* 输入框下积分余额(参照 work composerFooter):chip + hover 弹 PointsStatusCard */}
            {credits &&
              credits.total_quota != null &&
              credits.consumed != null && (
                <div className="aiClerk-homeV3__points">
                  <div
                    className="aiClerk-homeV3__points-hover"
                    onMouseEnter={() => setPointsCardVisible(true)}
                    onMouseLeave={() => setPointsCardVisible(false)}
                  >
                    {pointsCardVisible && (
                      <div className="aiClerk-homeV3__points-popover">
                        <PointsStatusCard
                          vipKind={vipKind}
                          total={credits.total_quota}
                          used={credits.consumed}
                          perQueryCost={SINGLE_QUERY_COST}
                          onCtaClick={handlePointsCta}
                        />
                      </div>
                    )}
                    <PointsEntryChip
                      vipKind={vipKind}
                      total={credits.total_quota}
                      used={credits.consumed}
                      perQueryCost={SINGLE_QUERY_COST}
                      onCtaClick={handlePointsCta}
                    />
                  </div>
                </div>
              )}
          </div>
        </aside>

        {/* 右:新用户引导 / 老用户模板+最近(上传入口已移到左栏顶部) */}
        <div className="aiClerk-homeV3__main">
          {/* 礼盒入口:非会员且积分未消耗过时展示 */}
          {!vipKind && (credits?.consumed ?? 1) <= 0 && (
            <button
              type="button"
              className="aiClerk-homeV3__gift-entry"
              onClick={() => {
                trackClick('formFiller', 'gift_entry', {
                  requirement_id: 1103,
                  track_sign: 'aai.b8134.click.ai_click_web_ai_form_filler'
                })
                setGiftOpen(true)
              }}
              aria-label="新用户福利"
            >
              <GiftFilled />
              <span className="aiClerk-homeV3__gift-entry-dot" />
            </button>
          )}
          {hasHistory ? (
            <div className="aiClerk-homeV3__history">
              {skills.length > 0 && (
                <section className="aiClerk-homeV3__panel">
                  <div className="aiClerk-homeV3__panel-head">
                    <span className="aiClerk-homeV3__panel-bar" />
                    <h2 className="aiClerk-homeV3__panel-title">我的模板</h2>
                  </div>
                  <div className="aiClerk-homeV3__tpl-list">
                    {skills.map((sk) => (
                      <div key={sk.name} className="aiClerk-homeV3__tpl">
                        <span className="aiClerk-homeV3__tpl-icon">
                          <FileTextFilled />
                        </span>
                        <div className="aiClerk-homeV3__tpl-main">
                          <div className="aiClerk-homeV3__tpl-name">
                            {sk.display_name}
                          </div>
                          {sk.updated_at && (
                            <div className="aiClerk-homeV3__tpl-meta">
                              {formatSessionTime(sk.updated_at)}
                            </div>
                          )}
                        </div>
                        <div className="aiClerk-homeV3__tpl-actions">
                          <button
                            type="button"
                            className="aiClerk-homeV3__tpl-use"
                            onClick={async () => {
                              const ready = await ensureCreditsReady()
                              if (!ready.ok) return
                              if ((ready.credits?.available ?? 0) <= 0) {
                                pendingStartRef.current = sk
                                setCreditsModalVisible(true)
                                return
                              }
                              startTemplate(sk)
                            }}
                          >
                            继续用
                          </button>
                          <button
                            type="button"
                            className="aiClerk-homeV3__tpl-icbtn"
                            aria-label="编辑"
                            onClick={() => {
                              trackClick('formFiller', 'edit_template', {
                                requirement_id: 1103,
                                track_sign:
                                  'aai.b8134.click.ai_click_web_ai_form_filler',
                                string1: sk.name
                              })
                              setEditingSkill(sk)
                            }}
                          >
                            <EditOutlined />
                          </button>
                          <button
                            type="button"
                            className="aiClerk-homeV3__tpl-icbtn aiClerk-homeV3__tpl-icbtn--danger"
                            aria-label="删除"
                            onClick={() => {
                              trackClick('formFiller', 'delete_template', {
                                requirement_id: 1103,
                                track_sign:
                                  'aai.b8134.click.ai_click_web_ai_form_filler',
                                string1: sk.name
                              })
                              trackDialogShow(
                                'formFiller',
                                'confirm_delete_template',
                                {
                                  requirement_id: 1103,
                                  track_sign:
                                    'aai.b8134.pageview.ai_dialog_show_web_ai_form_filler'
                                }
                              )
                              Modal.confirm({
                                title: `删除模板「${
                                  sk.display_name || sk.name
                                }」?`,
                                content:
                                  '删除后无法恢复，模板里的示例文件 / 业务描述都会一起删掉。',
                                okText: '删除',
                                okType: 'danger',
                                cancelText: '取消',
                                onOk: async () => {
                                  trackDialogClick(
                                    'formFiller',
                                    'confirm_delete_template',
                                    'confirm',
                                    {
                                      requirement_id: 1103,
                                      track_sign:
                                        'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
                                    }
                                  )
                                  try {
                                    await xheyAi.deleteUserSkill(sk.name)
                                    message.success(
                                      `已删除「${sk.display_name || sk.name}」`
                                    )
                                    loadSkills()
                                  } catch (e) {
                                    message.error(
                                      `删除失败: ${
                                        e instanceof Error
                                          ? e.message
                                          : String(e)
                                      }`
                                    )
                                  }
                                },
                                onCancel: () => {
                                  trackDialogClick(
                                    'formFiller',
                                    'confirm_delete_template',
                                    'cancel',
                                    {
                                      requirement_id: 1103,
                                      track_sign:
                                        'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
                                    }
                                  )
                                }
                              })
                            }}
                          >
                            <DeleteOutlined />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {recentFiles.length > 0 && (
                <section className="aiClerk-homeV3__panel">
                  <div className="aiClerk-homeV3__panel-head">
                    <span className="aiClerk-homeV3__panel-bar" />
                    <h2 className="aiClerk-homeV3__panel-title">
                      最近填好的文档
                    </h2>
                  </div>
                  <div className="aiClerk-homeV3__recent-list">
                    {recentFiles.map((f) => {
                      const iconStyle = pickFileIconStyle(f.filename)
                      const inner = (
                        <>
                          <span
                            className="aiClerk-homeV3__recent-icon"
                            style={{
                              background: iconStyle.bg,
                              color: iconStyle.color
                            }}
                          >
                            <FileTypeIcon name={iconStyle.type} />
                          </span>
                          <div className="aiClerk-homeV3__recent-main">
                            <div
                              className="aiClerk-homeV3__recent-name"
                              title={f.filename}
                            >
                              {f.filename}
                            </div>
                            <div className="aiClerk-homeV3__recent-meta">
                              {[
                                formatSessionTime(f.mtime_ms),
                                formatBytes(f.size_bytes)
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                          </div>
                          <span className="aiClerk-homeV3__recent-action">
                            {f.download_url ? (
                              <DownloadOutlined />
                            ) : (
                              <span className="aiClerk-homeV3__recent-disabled">
                                链接缺失
                              </span>
                            )}
                          </span>
                        </>
                      )
                      if (!f.download_url) {
                        return (
                          <div
                            key={f.path}
                            className="aiClerk-homeV3__recent is-disabled"
                          >
                            {inner}
                          </div>
                        )
                      }
                      if (!canExportShare) {
                        return (
                          <div
                            key={f.path}
                            className="aiClerk-homeV3__recent"
                            onClick={() => {
                              trackClick('formFiller', 'download_doc_blocked', {
                                requirement_id: 1103,
                                track_sign:
                                  'aai.b8134.click.ai_click_web_ai_form_filler',
                                string1: f.path
                              })
                              payment.openVipByKind(
                                CONFIG.VIP_KIND.VVIP,
                                'aiFormFiller',
                                false,
                                aiClerkPayContent({
                                  isVip: vipKind > 0,
                                  vipKind,
                                  usedPoints: credits?.consumed ?? 0
                                })
                              )
                            }}
                          >
                            {inner}
                          </div>
                        )
                      }
                      return (
                        <a
                          key={f.path}
                          href={f.download_url}
                          download={f.filename}
                          className="aiClerk-homeV3__recent"
                          onClick={() =>
                            trackClick('formFiller', 'download_doc', {
                              requirement_id: 1103,
                              track_sign:
                                'aai.b8134.click.ai_click_web_ai_form_filler',
                              string1: f.path
                            })
                          }
                        >
                          {inner}
                        </a>
                      )
                    })}
                  </div>
                </section>
              )}
            </div>
          ) : !isLoading ? (
            <section className="aiClerk-homeV3__guide">
              {/* ── Sec1 Hero ── */}
              <div className="aiClerk-homeV3__pf-hero">
                <h1 className="aiClerk-homeV3__pf-h1">
                  上传模板，AI 把你的照片和数据
                  <em className="aiClerk-homeV3__pf-brand">
                    自动填表、写汇报、做拼图
                  </em>
                </h1>
                <p className="aiClerk-homeV3__pf-sub">
                  支持 Excel 表格、Word 汇报、照片拼图——AI
                  读懂每一列、每一段要什么，从今日水印相机的照片和数据里取对内容，填得准、对得上。没有现成模板？描述一句也能生成。
                </p>
                <div className="aiClerk-homeV3__pf-btns">
                  <button
                    type="button"
                    className="aiClerk-homeV3__pf-btn-primary"
                    onClick={handlePickTemplate}
                    disabled={uploading}
                  >
                    <UploadOutlined />
                    上传表格，试填一份
                  </button>
                  <button
                    type="button"
                    className="aiClerk-homeV3__pf-btn-ghost"
                    onClick={handleWatchDemo}
                  >
                    <PlayCircleOutlined />看 AI 怎么填（30 秒）
                  </button>
                </div>
                <div className="aiClerk-homeV3__pf-trust">
                  <span className="aiClerk-homeV3__pf-avatars">
                    {[0, 1, 2].map((i) => {
                      const idx = (avatarOffset + i) % TRUST_NAMES.length
                      return (
                        <span
                          key={i}
                          style={{
                            background: TRUST_COLORS[idx % TRUST_COLORS.length]
                          }}
                        >
                          {TRUST_NAMES[idx]}
                        </span>
                      )
                    })}
                  </span>
                  2 万+ 团队在用 · 平均省 80% 填表时间
                </div>

                <HeroCarousel />
              </div>

              {/* ── Sec2 四步 + 信任 pill（前移：消解「不知道从哪开始」的第一焦虑）── */}
              <div className="aiClerk-homeV3__pf-sec aiClerk-homeV3__pf-sec--gray">
                <div className="aiClerk-homeV3__pf-sec-head">
                  <span className="aiClerk-homeV3__pf-eyebrow">四步搞定</span>
                  <h2 className="aiClerk-homeV3__pf-h2">
                    从上传到交付，最快几分钟
                  </h2>
                </div>
                <div className="aiClerk-homeV3__pf-steps">
                  {PF_STEPS.map((s, i) => (
                    <div key={s.title} className="aiClerk-homeV3__pf-step">
                      <span className="aiClerk-homeV3__pf-step-node">
                        {i + 1}
                      </span>
                      <span className="aiClerk-homeV3__pf-step-ic">
                        <s.icon />
                      </span>
                      <div className="aiClerk-homeV3__pf-step-text">
                        <div className="aiClerk-homeV3__pf-step-title">
                          {s.title}
                        </div>
                        <div className="aiClerk-homeV3__pf-step-desc">
                          {s.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="aiClerk-homeV3__pf-trustpill">
                  <SafetyOutlined />
                  AI
                  不瞎填，会先跟你确认每一张照片、人名和时间——确认了才落到成品里
                </div>
              </div>

              {/* ── Sec3 数据映射 + 表单库 ── */}
              <div className="aiClerk-homeV3__pf-sec">
                <div className="aiClerk-homeV3__pf-sec-head">
                  <span className="aiClerk-homeV3__pf-eyebrow">
                    一份数据，填进任意表
                  </span>
                  <h2 className="aiClerk-homeV3__pf-h2">
                    你拍下的每个字段，AI 自动填进表格、写进汇报
                  </h2>
                  <p className="aiClerk-homeV3__pf-sub2">
                    时间、地点、经纬度、真实海拔、天气、作业人员、水印名称……今日水印相机记录的字段，AI
                    自动填进 Excel、写进 Word 汇报、排进照片拼图，不挑模板。
                  </p>
                </div>

                {/* 数据映射:照片字段 → AI 匹配 → 表格(竖排适配窄栏) */}
                <div className="aiClerk-homeV3__pf-map">
                  <div className="aiClerk-homeV3__pf-mapcard">
                    <div className="aiClerk-homeV3__pf-mapcard-head">
                      <span>
                        <CameraOutlined /> 照片自带数据
                      </span>
                      <em>来自团队相册</em>
                    </div>
                    <div
                      className="aiClerk-homeV3__pf-thumb"
                      style={{ backgroundImage: `url(${dataSamplePhoto})` }}
                    >
                      <span className="aiClerk-homeV3__pf-thumb-wm">
                        真实海拔 576.4m · 18:52
                      </span>
                    </div>
                    <div className="aiClerk-homeV3__pf-fields">
                      {PF_PHOTO_FIELDS.map((f) => (
                        <span key={f} className="aiClerk-homeV3__pf-field">
                          <i className="aiClerk-homeV3__pf-field-dot" />
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="aiClerk-homeV3__pf-mapconn">
                    <span className="aiClerk-homeV3__pf-mapconn-line" />
                    <span className="aiClerk-homeV3__pf-mapai">
                      <StarFilled /> AI 自动对应 · 5 列已匹配
                    </span>
                    <DownOutlined className="aiClerk-homeV3__pf-mapconn-arrow" />
                  </div>
                  <div className="aiClerk-homeV3__pf-ledger">
                    <div className="aiClerk-homeV3__pf-ledger-title">
                      <b>现场作业影像台账 · 2026-04</b>
                      <span>86 条</span>
                    </div>
                    <div className="aiClerk-homeV3__pf-sheet">
                      <div className="aiClerk-homeV3__pf-sheet-row aiClerk-homeV3__pf-sheet-row--head">
                        {PF_LEDGER_HEADERS.map((h) => (
                          <span key={h}>{h}</span>
                        ))}
                      </div>
                      {PF_LEDGER_ROWS.map((row, r) => (
                        <div key={r} className="aiClerk-homeV3__pf-sheet-row">
                          {row.map((v, c) => (
                            <span
                              key={c}
                              className={
                                c === 2
                                  ? 'aiClerk-homeV3__pf-sheet-cell--alt'
                                  : undefined
                              }
                            >
                              {v}
                            </span>
                          ))}
                          <span className="aiClerk-homeV3__pf-sheet-photo" />
                        </div>
                      ))}
                    </div>
                    <div className="aiClerk-homeV3__pf-ledger-foot">
                      <CheckCircleFilled /> 5 列已自动匹配 ·
                      每格都对位到正确的照片
                    </div>
                  </div>
                </div>

                {/* 表单库:一份数据自动适配的行业模板(分类标签墙) */}
                <SupportedTypes onPick={handleQuickFill} />
              </div>

              {/* ── Sec3 协作 + 对话(2 特性,各带 3 勾选) ── */}
              <div className="aiClerk-homeV3__pf-sec">
                {PF_FEATURES.map((f, i) => (
                  <div key={f.title} className="aiClerk-homeV3__pf-feature">
                    <div className="aiClerk-homeV3__pf-feature-txt">
                      <span
                        className="aiClerk-homeV3__pf-eyebrow"
                        style={{ color: f.accent, background: f.accentBg }}
                      >
                        {f.eyebrow}
                      </span>
                      <h2 className="aiClerk-homeV3__pf-h2">{f.title}</h2>
                      <p className="aiClerk-homeV3__pf-sub2">{f.desc}</p>
                      <div className="aiClerk-homeV3__pf-checks">
                        {f.checks.map((c) => (
                          <div key={c} className="aiClerk-homeV3__pf-check">
                            <span
                              className="aiClerk-homeV3__pf-check-ic"
                              style={{
                                color: f.accent,
                                background: f.accentBg
                              }}
                            >
                              <CheckOutlined />
                            </span>
                            {c}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="aiClerk-homeV3__pf-feature-vis">
                      {i === 0 ? (
                        <>
                          <span className="aiClerk-homeV3__pf-fv-label">
                            从照片库挑对的那张
                          </span>
                          <div className="aiClerk-homeV3__pf-fv-thumbs">
                            <span className="aiClerk-homeV3__pf-fv-thumb">
                              <PictureOutlined />
                            </span>
                            <span className="aiClerk-homeV3__pf-fv-thumb aiClerk-homeV3__pf-fv-thumb--on">
                              <PictureOutlined />
                            </span>
                            <span className="aiClerk-homeV3__pf-fv-thumb">
                              <PictureOutlined />
                            </span>
                          </div>
                          <DownOutlined className="aiClerk-homeV3__pf-fv-arrow" />
                          <span className="aiClerk-homeV3__pf-fv-cell">
                            <CheckOutlined /> 已填进本格
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="aiClerk-homeV3__pf-fv-srccell">
                            <PictureOutlined /> B3 单元格
                          </span>
                          <span className="aiClerk-homeV3__pf-fv-bubble">
                            换成下午那张
                          </span>
                          <span className="aiClerk-homeV3__pf-fv-done">
                            <CheckOutlined /> 已重填
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Sec3.5 预览 / 编辑页能力(功能介绍) ── */}
              <div className="aiClerk-homeV3__pf-sec aiClerk-homeV3__pf-sec--gray">
                <div className="aiClerk-homeV3__pf-sec-head">
                  <span className="aiClerk-homeV3__pf-eyebrow">不止于填</span>
                  <h2 className="aiClerk-homeV3__pf-h2">
                    填完，在预览区直接改
                  </h2>
                  <p className="aiClerk-homeV3__pf-sub2">
                    成品只是开始——在预览区换图、增删行列、改写段落、调美观，跟
                    AI 说一句，它帮你改好。
                  </p>
                </div>

                {/* 预览区编辑窗 mock:工具栏 + 迷你表 + 对话框,呼应「在预览区直接改」 */}
                <div className="aiClerk-homeV3__pf-editmock">
                  <div className="aiClerk-homeV3__pf-em-bar">
                    <div className="aiClerk-homeV3__pf-em-barleft">
                      <span className="aiClerk-homeV3__pf-em-dot aiClerk-homeV3__pf-em-dot--r" />
                      <span className="aiClerk-homeV3__pf-em-dot aiClerk-homeV3__pf-em-dot--y" />
                      <span className="aiClerk-homeV3__pf-em-dot aiClerk-homeV3__pf-em-dot--g" />
                      <span className="aiClerk-homeV3__pf-em-file">
                        巡检台账.xlsx
                      </span>
                    </div>
                  </div>

                  <div className="aiClerk-homeV3__pf-em-table">
                    <div className="aiClerk-homeV3__pf-em-row aiClerk-homeV3__pf-em-row--head">
                      <span className="aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--pt">
                        点位
                      </span>
                      <span className="aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--st aiClerk-homeV3__pf-em-cell--hl">
                        状态
                      </span>
                      <span className="aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--ph">
                        照片
                      </span>
                    </div>
                    {[
                      ['1# 主变区', '正常'],
                      ['2# 母线桥', '正常'],
                      ['3# 开关柜', '异常'],
                      ['4# 配电室', '正常'],
                      ['5# 主控室', '正常']
                    ].map((row) => (
                      <div key={row[0]} className="aiClerk-homeV3__pf-em-row">
                        <span className="aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--pt">
                          {row[0]}
                        </span>
                        <span
                          className={`aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--st${
                            row[1] === '异常'
                              ? ' aiClerk-homeV3__pf-em-cell--err'
                              : ' aiClerk-homeV3__pf-em-cell--alt'
                          }`}
                        >
                          {row[1]}
                        </span>
                        <span className="aiClerk-homeV3__pf-em-cell aiClerk-homeV3__pf-em-cell--ph">
                          <span className="aiClerk-homeV3__pf-em-photo">
                            <PictureOutlined />
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="aiClerk-homeV3__pf-em-chat">
                    <span className="aiClerk-homeV3__pf-em-ph">
                      把「状态」这列都填上，再把表头调成蓝底…
                    </span>
                  </div>
                </div>

                <div className="aiClerk-homeV3__pf-caps">
                  {PF_CAPS.map((c) => (
                    <div key={c.title} className="aiClerk-homeV3__pf-cap">
                      <span
                        className="aiClerk-homeV3__pf-cap-ic"
                        style={{ color: c.color, background: c.bg }}
                      >
                        <c.icon />
                      </span>
                      <div className="aiClerk-homeV3__pf-cap-text">
                        <div className="aiClerk-homeV3__pf-cap-title">
                          {c.title}
                        </div>
                        <div className="aiClerk-homeV3__pf-cap-desc">
                          {c.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Sec6 收尾 CTA ── */}
              <div className="aiClerk-homeV3__pf-cta">
                <span className="aiClerk-homeV3__pf-cta-badge">
                  <CheckCircleFilled /> 填得准 · 写得好 · 改得动
                </span>
                <h2 className="aiClerk-homeV3__pf-cta-title">
                  把第一张表，交给 AI
                </h2>
                <p className="aiClerk-homeV3__pf-cta-sub">
                  上传 Excel / Word 模板，AI
                  帮你填好、写好；没有模板，描述一句也能生成。
                </p>
                <div className="aiClerk-homeV3__pf-cta-btns">
                  <button
                    type="button"
                    className="aiClerk-homeV3__pf-cta-primary"
                    onClick={handlePickTemplate}
                    disabled={uploading}
                  >
                    <UploadOutlined /> 上传表格，试填一份
                  </button>
                  <button
                    type="button"
                    className="aiClerk-homeV3__pf-cta-ghost"
                    onClick={handleDescribeCta}
                  >
                    <MessageOutlined /> 描述需求生成
                  </button>
                </div>
                <div className="aiClerk-homeV3__pf-cta-risk">
                  <SafetyOutlined />
                  你的照片和数据不外泄、不用于训练
                </div>
              </div>
            </section>
          ) : null}
        </div>
      </div>

      {/* ── 上传模板后弹窗(与 V1/V2 一致,复用 index.scss 的 start-modal 样式) ── */}
      <Modal
        title={null}
        open={startModalOpen}
        onCancel={handleStartModalClose}
        mask={{ closable: !uploading }}
        closable={!uploading}
        footer={null}
        width={720}
        destroyOnHidden
        zIndex={790}
        wrapClassName="aiClerk-home__start-modal-wrap"
      >
        <div className="aiClerk-home__start-modal-head">
          <h2 className="aiClerk-home__start-modal-h2">上传文档</h2>
        </div>

        <div className="aiClerk-home__start-modal-block">
          <div className="aiClerk-home__start-modal-block-label">上传文档</div>
          {templateFile ? (
            <div className="aiClerk-home__start-modal-file-card">
              <div className="aiClerk-home__start-modal-file-icon-wrap">
                <FileTypeIcon
                  name={
                    /\.(docx|doc)$/i.test(templateFile.name)
                      ? 'file-word'
                      : /\.(pptx|ppt)$/i.test(templateFile.name)
                        ? 'file-ppt'
                        : 'file-excel'
                  }
                  className="aiClerk-home__start-modal-file-icon"
                />
              </div>
              <div className="aiClerk-home__start-modal-file-info">
                <div
                  className="aiClerk-home__start-modal-file-name"
                  title={templateFile.name}
                >
                  {templateFile.name}
                </div>
                <div className="aiClerk-home__start-modal-file-meta">
                  <span>{(templateFile.size / 1024).toFixed(0)} KB</span>
                </div>
              </div>
              <button
                type="button"
                className="aiClerk-home__start-modal-file-close"
                onClick={handleClearTemplateInModal}
                aria-label="移除模板"
                disabled={uploading}
              >
                ×
              </button>
            </div>
          ) : (
            <div className="aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--empty">
              <span className="aiClerk-home__start-modal-sample-icon">
                <ArrowUpOutlined />
              </span>
              <div className="aiClerk-home__start-modal-sample-text">
                <div className="aiClerk-home__start-modal-sample-title">
                  上传一份要填的文档
                </div>
                <div className="aiClerk-home__start-modal-sample-desc">
                  支持{' '}
                  <span className="aiClerk-home__start-modal-ext">.docx</span>{' '}
                  <span className="aiClerk-home__start-modal-ext">.xlsx</span>{' '}
                  格式
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickTemplate}
                disabled={uploading}
              >
                上传文档
              </Button>
            </div>
          )}
        </div>

        <div className="aiClerk-home__start-modal-block">
          <div className="aiClerk-home__start-modal-block-label">
            也可上传
            <strong className="aiClerk-home__start-modal-block-label-em">
              【样例】
            </strong>
            提高填写准确率
            <span className="aiClerk-home__start-modal-block-label-tag">
              可选
            </span>
          </div>

          {sampleFile ? (
            <div
              className={`aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--filled${
                dragOverSample ? ' is-drag-over' : ''
              }`}
              onDragOver={handleSampleDragOver}
              onDragLeave={handleSampleDragLeave}
              onDrop={handleSampleDrop}
            >
              <div className="aiClerk-home__start-modal-file-icon-wrap">
                <FileTypeIcon
                  name={
                    /\.(docx|doc)$/i.test(sampleFile.name)
                      ? 'file-word'
                      : 'file-excel'
                  }
                  className="aiClerk-home__start-modal-file-icon"
                />
              </div>
              <div className="aiClerk-home__start-modal-file-info">
                <div
                  className="aiClerk-home__start-modal-file-name"
                  title={sampleFile.name}
                >
                  {sampleFile.name}
                </div>
                <div className="aiClerk-home__start-modal-file-meta">
                  {(sampleFile.size / 1024).toFixed(0)} KB
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickSample}
                disabled={uploading}
              >
                上传样例
              </Button>
            </div>
          ) : (
            <div
              className={`aiClerk-home__start-modal-sample-card aiClerk-home__start-modal-sample-card--empty${
                dragOverSample ? ' is-drag-over' : ''
              }`}
              onDragOver={handleSampleDragOver}
              onDragLeave={handleSampleDragLeave}
              onDrop={handleSampleDrop}
            >
              <span className="aiClerk-home__start-modal-sample-icon">
                <ThunderboltFilled />
              </span>
              <div className="aiClerk-home__start-modal-sample-text">
                <div className="aiClerk-home__start-modal-sample-title">
                  上传样例,让识别更准
                </div>
                <div className="aiClerk-home__start-modal-sample-desc">
                  再传一份「之前填好的」样例,AI 会对比学习你的填写习惯,
                  提高填写正确率
                </div>
              </div>
              <Button
                type="primary"
                className="aiClerk-home__start-modal-sample-btn"
                onClick={handlePickSample}
                disabled={uploading}
              >
                上传样例
              </Button>
            </div>
          )}
        </div>

        {(probing || probeResult) && (
          <div className="aiClerk-home__probe" style={{ marginTop: 12 }}>
            {probing && (
              <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--neutral">
                <LoadingOutlined /> 正在检测样例与模板的字段匹配…
              </div>
            )}
            {!probing && probeResult && !probeResult.parseError && (
              <>
                {probeResult.matchPercent >= 70 && (
                  <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--ok">
                    <CheckCircleOutlined /> 字段匹配良好(
                    {probeResult.matchedHeaders}/{probeResult.totalHeaders}{' '}
                    列同名)
                  </div>
                )}
                {probeResult.matchPercent >= 30 &&
                  probeResult.matchPercent < 70 && (
                    <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--warn">
                      <WarningOutlined /> 仅 {probeResult.matchPercent}%
                      列对得上(
                      {probeResult.matchedHeaders}/{probeResult.totalHeaders}
                      ),确认是同类型表?
                    </div>
                  )}
                {probeResult.matchPercent < 30 &&
                  probeResult.totalHeaders > 0 && (
                    <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--err">
                      <CloseCircleOutlined /> 看起来不是同类型表(
                      {probeResult.matchedHeaders}/{probeResult.totalHeaders}{' '}
                      列匹配)
                    </div>
                  )}
                {probeResult.reversed && (
                  <div className="aiClerk-home__probe-pill aiClerk-home__probe-pill--info">
                    <SwapOutlined /> 模板槽里的文件填得更满,是不是放反了?
                    <button
                      type="button"
                      className="aiClerk-home__probe-swap"
                      onClick={handleSwapTemplateAndSample}
                      disabled={uploading}
                    >
                      一键互换
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <input
          ref={sampleInputRef}
          type="file"
          accept={ACCEPT_TYPES}
          hidden
          onChange={handleSampleChange}
        />

        <div className="aiClerk-home__start-modal-footer">
          <Button
            type="primary"
            size="large"
            className="aiClerk-home__start-btn aiClerk-home__start-modal-cta"
            onClick={() => handleStart()}
            disabled={!templateFile || uploading}
            loading={uploading}
          >
            {uploading ? '上传中…' : '开始识别'}
          </Button>
        </div>
      </Modal>

      {/* ── 积分不足拦截弹窗 + 增购积分包 ── */}
      <CreditsExhaustedModal
        visible={creditsModalVisible}
        onClose={() => setCreditsModalVisible(false)}
        available={credits?.available ?? 0}
        cost={SINGLE_QUERY_COST}
        vipKind={vipKind}
        onBuyPack={() => {
          setCreditsModalVisible(false)
          setStartModalOpen(false)
          setPointsPackVisible(true)
        }}
        onUpgrade={() => {
          setCreditsModalVisible(false)
          setStartModalOpen(false)
          payment.openVipByKind(
            vipKind || CONFIG.VIP_KIND.VIP,
            'aiFormFiller',
            false,
            aiClerkPayContent({
              isVip: vipKind > 0,
              vipKind,
              usedPoints: credits?.consumed ?? 0
            })
          )
        }}
      />
      <PointsPackModal
        visible={pointsPackVisible}
        onClose={() => setPointsPackVisible(false)}
        source="aiFormFillerBuyCredits"
        onPaymentSuccess={() => {
          setPointsPackVisible(false)
          refreshCredits()
          runPendingStart()
        }}
      />
      <EditSkillModal
        skill={editingSkill}
        open={!!editingSkill}
        onClose={() => setEditingSkill(null)}
        onSaved={() => {
          setEditingSkill(null)
          loadSkills()
        }}
      />
      <DemoReplayModal
        visible={demoVisible}
        onClose={() => setDemoVisible(false)}
      />

      <GiftModal
        visible={giftOpen}
        onClose={() => {
          trackDialogClick('formFiller', 'gift_modal', 'close', {
            requirement_id: 1103,
            track_sign: 'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
          })
          setGiftOpen(false)
        }}
        onLater={() => {
          trackDialogClick('formFiller', 'gift_modal', 'later', {
            requirement_id: 1103,
            track_sign: 'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
          })
          setGiftOpen(false)
        }}
        onStart={() => {
          trackDialogClick('formFiller', 'gift_modal', 'start', {
            requirement_id: 1103,
            track_sign: 'aai.b8134.click.ai_dialog_click_web_ai_form_filler'
          })
          setGiftOpen(false)
          handlePickTemplate()
        }}
      />
    </div>
  )
}

export default HomeV3
